import { useEffect, useState, useCallback } from 'react';
import { PRODUCTS as SEED_PRODUCTS, CATEGORIES, BIGTIME_WHATSAPP } from '@/mock/products';
import * as ent from '@/services/entitiesService';
import { getToken } from '@/services/authService';

const LS_PRODUCTS = 'bigtime_products_v1';
const LS_BANNERS = 'bigtime_banners_v1';
const LS_STORES = 'bigtime_stores_v1';
const LS_SELECTED_STORE = 'bigtime_selected_store_v1';
const LS_WISHLIST = 'bigtime_wishlist_v1';
const LS_REVIEWS = 'bigtime_reviews_v1';
const LS_URGENCY = 'bigtime_urgency_v1';
const LS_ORDERS = 'bigtime_orders_v1';

// Order lifecycle (iter 31): new 9-column Kanban flow. Old values
// ('Pendiente','Confirmada','Pago en Verificación','Listo para Retiro') are
// still valid in STATUS_COLORS so historical rows render correctly.
export const ORDER_STATUSES = [
  'Recibido',
  'Confirmar Pago',
  'En Preparación',
  'Preparado',
  'Pendiente de Pago',
  'Pagado',
  'Listo para Enviar',
  'En Agencia',
  'Entregado',
  'Finalizado',
  'Cancelado',
];
// Kanban shows the 9 workflow columns in order (Cancelado is a side-state,
// not a column).
export const KANBAN_STATUSES = [
  'Recibido',
  'Confirmar Pago',
  'En Preparación',
  'Preparado',
  'Pendiente de Pago',
  'Pagado',
  'Listo para Enviar',
  'En Agencia',
  'Entregado',
  'Finalizado',
];
export const STATUS_COLORS = {
  Recibido: 'bg-stone-200 text-stone-800 border-stone-300',
  'Confirmar Pago': 'bg-sky-100 text-sky-800 border-sky-300',
  'En Preparación': 'bg-yellow-100 text-yellow-800 border-yellow-300',
  Preparado: 'bg-lime-100 text-lime-800 border-lime-300',
  'Pendiente de Pago': 'bg-orange-100 text-orange-800 border-orange-300',
  Pagado: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Listo para Enviar': 'bg-teal-100 text-teal-800 border-teal-300',
  'En Agencia': 'bg-blue-100 text-blue-800 border-blue-300',
  Entregado: 'bg-green-100 text-green-800 border-green-300',
  Finalizado: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  Cancelado: 'bg-red-100 text-red-800 border-red-300',
  // Legacy fallbacks (map old statuses so historical rows still render nicely)
  Pendiente: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  Confirmada: 'bg-slate-100 text-slate-700 border-slate-300',
  Enviado: 'bg-blue-100 text-blue-800 border-blue-300',
  Enviada: 'bg-blue-100 text-blue-800 border-blue-300',
  Entregada: 'bg-green-100 text-green-800 border-green-300',
  Cancelada: 'bg-red-100 text-red-800 border-red-300',
  'Pago en Verificación': 'bg-orange-100 text-orange-800 border-orange-300',
  'Listo para Retiro': 'bg-teal-100 text-teal-800 border-teal-300',
};
export const STATUS_WA_MESSAGE = {
  Recibido: 'Hola {name} 👋 Recibimos tu pedido *{code}* en BIGTIME. Estamos revisándolo, te avisamos apenas confirmemos el pago.',
  'Confirmar Pago': '💳 Hola {name}, estamos verificando el pago de tu pedido *{code}*. Te avisamos apenas esté confirmado.',
  'En Preparación': '¡Buenas noticias {name}! 🎁 Tu pedido *{code}* ya está *en preparación*. Pronto lo enviamos y te compartimos el número de guía.',
  Preparado: '✨ {name}, tu pedido *{code}* ya está *preparado* y esperando el pago para salir a enviarse.',
  'Pendiente de Pago': '💳 Hola {name}, tu pedido *{code}* está *pendiente de pago*. Por favor confírmanos el comprobante para continuar.',
  Pagado: '✅ ¡Recibimos tu pago {name}! Tu pedido *{code}* ya está *pagado* y listo para el próximo paso.',
  'Listo para Enviar': '📦 {name}, tu pedido *{code}* está *listo para enviarse*. Muy pronto lo llevaremos a la agencia.',
  'En Agencia': '🚚 {name}, tu pedido *{code}* ya fue entregado a la agencia. Te compartiremos el número de guía en breve.',
  Entregado: '🎉 {name}, tu pedido *{code}* fue *entregado*. ¡Gracias por confiar en BIGTIME! ⭐',
  Finalizado: '✨ Tu pedido *{code}* está *finalizado*. ¡Fue un placer atenderte, {name}! Vuelve cuando quieras.',
  Cancelado: 'Hola {name}, tu pedido *{code}* fue *cancelado*. Si necesitas ayuda o quieres retomarlo, avísanos por aquí.',
  // Legacy templates preserved for backward-compat rows
  'Pago en Verificación': 'Hola {name}, tu pedido *{code}* está con nosotros y estamos *verificando el pago*.',
  Enviado: '📦 {name}, tu pedido *{code}* ya está *enviado*.',
  'Listo para Retiro': '{name}, tu pedido *{code}* ya está *listo para retiro*.',
};

const DEFAULT_STORES = [
  { id: 's1', name: 'BIGTIME Caracas', whatsapp: '584123120579', address: 'Sabana Grande, Caracas', username: 'caracas', availableProductIds: 'all' },
  { id: 's2', name: 'BIGTIME Valencia', whatsapp: '584241234567', address: 'Av. Bolivar Norte, Valencia', username: 'valencia', availableProductIds: 'all' },
  { id: 's3', name: 'BIGTIME Maracaibo', whatsapp: '584261234567', address: 'Centro, Maracaibo', username: 'maracaibo', availableProductIds: 'all' },
];

const DEFAULT_BANNERS = [
  { id: 'b1', title: 'Ofertas de la semana!', subtitle: 'Hasta 30% OFF en Belleza y Skincare', cta: 'Ver ofertas', href: '/catalogo?cat=belleza', image: 'https://images.unsplash.com/photo-1512496015851-a90fb38ba796?w=1600', bg: '#F8D13F', textColor: '#1B2A4A' },
  { id: 'b2', title: 'Nueva coleccion Relojeria', subtitle: 'Smartwatches y relojes clasicos', cta: 'Explorar', href: '/catalogo?cat=relojeria', image: 'https://images.unsplash.com/photo-1617043983671-adaadcaa2460?w=1600', bg: '#1B2A4A', textColor: '#F8D13F' },
  { id: 'b3', title: 'Electronicos al mejor precio', subtitle: 'Audifonos, cargadores, power banks', cta: 'Comprar ahora', href: '/catalogo?cat=electronicos', image: 'https://images.pexels.com/photos/33797659/pexels-photo-33797659.jpeg?auto=compress&cs=tinysrgb&w=1600', bg: '#F8D13F', textColor: '#1B2A4A' },
];

const DEFAULT_URGENCY = {
  enabled: true,
  message: '🔥 OFERTA FLASH: 20% OFF en toda la tienda solo por hoy',
  cta: 'Comprar ahora',
  href: '/catalogo',
  bg: '#DC2626',
  textColor: '#FFFFFF',
};

const load = (key, fallback) => {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : fallback; } catch { return fallback; }
};
const save = (key, val) => {
  localStorage.setItem(key, JSON.stringify(val));
  window.dispatchEvent(new CustomEvent('bigtime-ls-change', { detail: { key } }));
};

function useLSState(key, fallback) {
  const [value, setValue] = useState(() => load(key, fallback));
  useEffect(() => {
    // Re-sync in case value changed between initial render and effect registration
    setValue(load(key, fallback));
    const onStorage = (e) => { if (e.key === key) setValue(load(key, fallback)); };
    const onLocal = (e) => { if (e.detail?.key === key) setValue(load(key, fallback)); };
    window.addEventListener('storage', onStorage);
    window.addEventListener('bigtime-ls-change', onLocal);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('bigtime-ls-change', onLocal);
    };
    // eslint-disable-next-line
  }, [key]);
  const persist = useCallback((next) => { save(key, next); setValue(next); }, [key]);
  return [value, persist];
}

export function useProducts() {
  const [products, setProducts] = useState(() => load(LS_PRODUCTS, SEED_PRODUCTS));
  // `loading` = we haven't yet completed the first remote refresh. Used by the
  // catalog page to show a skeleton grid when there is no localStorage cache
  // to fall back on (returning users see the cached list instantly).
  const [loading, setLoading] = useState(() => !load(LS_PRODUCTS, null));

  const refresh = useCallback(async () => {
    try {
      const remote = await ent.svcListProducts();
      if (Array.isArray(remote)) {
        // Server is the source of truth (backend seeds a starter catalog on first run).
        save(LS_PRODUCTS, remote);
        setProducts(remote);
      }
    } catch {
      // offline: keep local cache
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const onLocal = (e) => { if (e.detail?.key === LS_PRODUCTS) setProducts(load(LS_PRODUCTS, SEED_PRODUCTS)); };
    window.addEventListener('bigtime-ls-change', onLocal);
    return () => window.removeEventListener('bigtime-ls-change', onLocal);
  }, [refresh]);

  const addProduct = async (p) => {
    const created = await ent.svcCreateProduct(p);
    setProducts((prev) => {
      // Dedupe by id in case a concurrent refresh already inserted it.
      const next = [created, ...prev.filter((x) => x.id !== created.id)];
      save(LS_PRODUCTS, next); return next;
    });
    return created;
  };
  const updateProduct = async (id, patch) => {
    const updated = await ent.svcUpdateProduct(id, patch);
    setProducts((prev) => { const next = prev.map((p) => p.id === id ? { ...p, ...updated } : p); save(LS_PRODUCTS, next); return next; });
  };
  const deleteProduct = async (id) => {
    await ent.svcDeleteProduct(id);
    setProducts((prev) => { const next = prev.filter((p) => p.id !== id); save(LS_PRODUCTS, next); return next; });
  };
  const resetProducts = () => { save(LS_PRODUCTS, SEED_PRODUCTS); setProducts(SEED_PRODUCTS); };
  const bulkUpdatePrices = async (ids, percent) => {
    await ent.svcBulkPrice(ids, percent); await refresh();
  };
  return { products, loading, addProduct, updateProduct, deleteProduct, resetProducts, bulkUpdatePrices, refresh };
}

export function useBanners() {
  const [banners, setBanners] = useState(() => load(LS_BANNERS, DEFAULT_BANNERS));
  const refresh = useCallback(async () => {
    try {
      // Prefer authenticated /admin/banners (shows inactive too) with graceful
      // fall-back to the public /banners endpoint.
      const token = localStorage.getItem('bigtime_token');
      const path = token ? '/admin/banners' : '/banners';
      const remote = await ent.api.get(path).then((r) => r.data).catch(() => null);
      if (Array.isArray(remote)) {
        setBanners(remote);
        save(LS_BANNERS, remote);
      }
    } catch { /* keep localStorage cache */ }
  }, []);
  useEffect(() => { refresh(); const t = setInterval(refresh, 60000); return () => clearInterval(t); }, [refresh]);

  const addBanner = async (b) => {
    const { data } = await ent.api.post('/banners', b);
    setBanners((prev) => { const n = [data, ...prev]; save(LS_BANNERS, n); return n; });
    return data;
  };
  const updateBanner = async (id, patch) => {
    const { data } = await ent.api.patch(`/banners/${id}`, patch);
    setBanners((prev) => { const n = prev.map((b) => b.id === id ? { ...b, ...data } : b); save(LS_BANNERS, n); return n; });
    return data;
  };
  const deleteBanner = async (id) => {
    await ent.api.delete(`/banners/${id}`);
    setBanners((prev) => { const n = prev.filter((b) => b.id !== id); save(LS_BANNERS, n); return n; });
  };
  const resetBanners = () => { setBanners(DEFAULT_BANNERS); save(LS_BANNERS, DEFAULT_BANNERS); };
  return { banners, addBanner, updateBanner, deleteBanner, resetBanners, refresh };
}

export function useStores() {
  const [stores, setStores] = useState(() => load(LS_STORES, DEFAULT_STORES));
  const refresh = useCallback(async () => {
    try {
      const remote = await ent.svcListStores();
      if (Array.isArray(remote) && remote.length > 0) {
        // Merge remote basic info with local fields like username/password for legacy compatibility
        const merged = remote.map((r) => ({
          id: r.id, name: r.name, whatsapp: r.whatsapp, address: r.address,
          username: r.username, password: (load(LS_STORES, []).find((x) => x.id === r.id)?.password) || '',
          availableProductIds: r.availableProductIds || 'all',
        }));
        save(LS_STORES, merged); setStores(merged);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.debug('stores refresh:', e?.message); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const addStore = async (s) => {
    const created = await ent.svcCreateStore({
      name: s.name, username: s.username, password: s.password,
      whatsapp: s.whatsapp || '', address: s.address || '',
    });
    const localItem = { id: created.id, name: created.name, whatsapp: created.whatsapp, address: created.address, username: created.username, password: s.password, availableProductIds: 'all' };
    setStores((prev) => { const next = [...prev, localItem]; save(LS_STORES, next); return next; });
    return created;
  };
  const updateStore = async (id, patch) => {
    const backendPatch = {};
    ['name', 'username', 'whatsapp', 'address'].forEach((k) => { if (patch[k] !== undefined) backendPatch[k] = patch[k]; });
    if (patch.password) backendPatch.password = patch.password;
    if (Object.keys(backendPatch).length) await ent.svcUpdateStore(id, backendPatch);
    setStores((prev) => { const next = prev.map((s) => s.id === id ? { ...s, ...patch } : s); save(LS_STORES, next); return next; });
  };
  const deleteStore = async (id) => {
    await ent.svcDeleteStore(id);
    setStores((prev) => { const next = prev.filter((s) => s.id !== id); save(LS_STORES, next); return next; });
  };
  const setStoreProducts = async (id, ids) => {
    await ent.svcSetStoreAvailability(id, ids);
    setStores((prev) => { const next = prev.map((s) => s.id === id ? { ...s, availableProductIds: ids } : s); save(LS_STORES, next); return next; });
  };
  return { stores, addStore, updateStore, deleteStore, setStoreProducts, refresh };
}

export function useSelectedStore() {
  const [selectedId, persist] = useLSState(LS_SELECTED_STORE, null);
  const { stores } = useStores();
  const store = stores.find((s) => s.id === selectedId) || null;
  return { store, selectedId, selectStore: persist, clear: () => persist(null) };
}

export function useAvailableProducts() {
  const { products, loading } = useProducts();
  const { store } = useSelectedStore();
  if (!store || store.availableProductIds === 'all') return { products, loading, store };
  const allowedSet = new Set(store.availableProductIds || []);
  return { products: products.filter((p) => allowedSet.has(p.id)), loading, store };
}

export function useOrders() {
  const [orders, setOrders] = useState(() => load(LS_ORDERS, []));

  const refresh = useCallback(async () => {
    if (!getToken()) return;  // public shoppers can't list orders
    try {
      const remote = await ent.svcListOrders();
      if (Array.isArray(remote)) {
        // Map backend camelCase differences (sales_type -> salesType, etc.) for compat
        const mapped = remote.map((o) => ({
          ...o,
          salesType: o.sales_type || o.salesType,
          storeId: o.store_id || o.storeId,
          createdAt: o.created_at || o.createdAt,
        }));
        save(LS_ORDERS, mapped); setOrders(mapped);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.debug('orders refresh:', e?.message); }
  }, []);
  useEffect(() => {
    refresh();
    // Sync across hook instances: when ANY caller writes to LS_ORDERS (e.g.
    // the cart drawer just created an order), every other useOrders instance
    // in the app (admin panel, store panel) pulls the fresh
    // list from localStorage immediately instead of waiting 30s.
    const onLocal = (e) => { if (e.detail?.key === LS_ORDERS) queueMicrotask(() => setOrders(load(LS_ORDERS, []))); };
    window.addEventListener('bigtime-ls-change', onLocal);
    const t = setInterval(refresh, 30000);
    return () => { window.removeEventListener('bigtime-ls-change', onLocal); clearInterval(t); };
  }, [refresh]);

  const addOrder = async (o) => {
    // Server-first: never fabricate a fake order. Bubble up errors so UI can react.
    const created = await ent.svcCreateOrder({
      store_id: o.storeId, customer: o.customer,
      items: o.items, subtotal: o.subtotal, sales_type: o.salesType || 'online',
      gift_card_code: o.giftCardCode || null,
      payments: o.payments || null,
    });
    const mapped = { ...created, salesType: created.sales_type, storeId: created.store_id, createdAt: created.created_at };
    // Use functional setState to avoid stale closure (previous impl overwrote
    // recent state when addOrder was called twice in quick succession).
    setOrders((prev) => {
      const next = [mapped, ...prev.filter((x) => x.id !== mapped.id)];
      save(LS_ORDERS, next);
      return next;
    });
    return mapped;
  };
  const updateStatus = async (id, status) => {
    await ent.svcUpdateOrder(id, { status });
    // Functional setState avoids stale-closure bugs when two updates race.
    setOrders((prev) => {
      const next = prev.map((o) => o.id === id ? { ...o, status, updatedAt: new Date().toISOString() } : o);
      save(LS_ORDERS, next);
      return next;
    });
  };
  const updateOrder = async (id, patch) => {
    const backendPatch = {};
    if (patch.salesType) backendPatch.sales_type = patch.salesType;
    if (patch.status) backendPatch.status = patch.status;
    if (patch.payment_confirmed !== undefined) backendPatch.payment_confirmed = patch.payment_confirmed;
    if (Object.keys(backendPatch).length) await ent.svcUpdateOrder(id, backendPatch);
    const localPatch = { ...patch };
    setOrders((prev) => {
      const next = prev.map((o) => o.id === id ? { ...o, ...localPatch } : o);
      save(LS_ORDERS, next);
      return next;
    });
  };
  const deleteOrder = async (id) => {
    await ent.svcDeleteOrder(id);
    setOrders((prev) => {
      const next = prev.filter((o) => o.id !== id);
      save(LS_ORDERS, next);
      return next;
    });
  };
  const forStore = (storeId) => orders.filter((o) => (o.storeId || o.store_id) === storeId);
  return { orders, addOrder, updateStatus, updateOrder, deleteOrder, forStore, refresh };
}

export function useCustomers() {
  const { orders } = useOrders();
  const map = new Map();
  orders.forEach((o) => {
    const key = o.customer?.cedula || o.customer?.phone || o.customer?.name;
    if (!key) return;
    const existing = map.get(key) || {
      key,
      name: o.customer.name,
      cedula: o.customer.cedula,
      phone: o.customer.phone,
      city: o.customer.city,
      orders: [],
      totalSpent: 0,
      lastOrder: o.createdAt,
    };
    existing.orders.push(o);
    // Cancelled sales must NEVER inflate lifetime spend — check BOTH legacy
    // feminine ('Cancelada') and current masculine ('Cancelado') spellings.
    if (o.status !== 'Cancelada' && o.status !== 'Cancelado') existing.totalSpent += o.subtotal || 0;
    if (o.createdAt > existing.lastOrder) existing.lastOrder = o.createdAt;
    map.set(key, existing);
  });
  return { customers: Array.from(map.values()).sort((a, b) => (b.lastOrder || '').localeCompare(a.lastOrder || '')) };
}

export function useWishlist() {
  const [ids, persist] = useLSState(LS_WISHLIST, []);
  const toggle = (id) => persist(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  const isFav = (id) => ids.includes(id);
  const clear = () => persist([]);
  return { ids, toggle, isFav, clear };
}

export function useReviews() {
  const [reviews, persist] = useLSState(LS_REVIEWS, []);
  const addReview = (r) => persist([{ ...r, id: `r_${Date.now()}`, date: new Date().toISOString() }, ...reviews]);
  const deleteReview = (id) => persist(reviews.filter((r) => r.id !== id));
  const forProduct = (pid) => reviews.filter((r) => r.productId === pid);
  const forStore = (sid) => reviews.filter((r) => r.storeId === sid && !r.productId);
  return { reviews, addReview, deleteReview, forProduct, forStore };
}

export function useUrgencyBanner() {
  const [urgency, persist] = useLSState(LS_URGENCY, DEFAULT_URGENCY);
  return { urgency, updateUrgency: persist };
}

// -------- Categories (server-managed, LS-cached, hardcoded fallback) --------
const LS_CATEGORIES = 'bigtime_categories_v1';

export function useCategories() {
  const [categories, setCategories] = useState(() => load(LS_CATEGORIES, CATEGORIES));
  const refresh = useCallback(async () => {
    try {
      const remote = await ent.svcListCategories();
      if (Array.isArray(remote) && remote.length) {
        save(LS_CATEGORIES, remote); setCategories(remote);
      }
    } catch (e) { if (process.env.NODE_ENV === 'development') console.debug('categories refresh:', e?.message); }
  }, []);
  useEffect(() => {
    refresh();
    // Defer setState so cross-component notifications from `save()` don't
    // land during another component's render commit (React warning otherwise).
    const onLocal = (e) => { if (e.detail?.key === LS_CATEGORIES) queueMicrotask(() => setCategories(load(LS_CATEGORIES, CATEGORIES))); };
    window.addEventListener('bigtime-ls-change', onLocal);
    return () => window.removeEventListener('bigtime-ls-change', onLocal);
  }, [refresh]);
  const addCategory = async (body) => {
    const created = await ent.svcCreateCategory(body);
    setCategories((prev) => {
      const next = [...prev.filter((c) => c.id !== created.id), created];
      save(LS_CATEGORIES, next); return next;
    });
    return created;
  };
  const updateCategory = async (id, patch) => {
    const updated = await ent.svcUpdateCategory(id, patch);
    setCategories((prev) => {
      const next = prev.map((c) => c.id === id ? { ...c, ...updated } : c);
      save(LS_CATEGORIES, next); return next;
    });
    return updated;
  };
  const deleteCategory = async (id) => {
    await ent.svcDeleteCategory(id);
    setCategories((prev) => {
      const next = prev.filter((c) => c.id !== id);
      save(LS_CATEGORIES, next); return next;
    });
  };
  return { categories, addCategory, updateCategory, deleteCategory, refresh };
}

export { CATEGORIES, BIGTIME_WHATSAPP };
